import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import { assertPortalLandingValue } from "@productlane/lib/src/portal-landing"

import type { MutatorTx, Schema } from "../schema"
import type {
	CreatePortalInstanceArgs,
	UpdatePortalInstanceArgs,
} from "../types"
import type { DeleteID } from "@rocicorp/zero"

export const portalInstanceMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreatePortalInstanceArgs
			ctx: ZeroJwtPayload
		}) => {
			// Optimistic client-side insert. Plan gating and slug validation happen
			// in the server override (packages/api/src/zero/mutators/portal-instances.ts).
			// `isDefault` is a legacy column — Root is the implicit default, so any
			// real PortalInstance row is just a named non-Root portal.
			assertPortalLandingValue(args.landingSection)
			const now = Date.now()

			await tx.mutate.portalInstances.insert({
				id: args.id,
				slug: args.slug,
				name: args.name,
				isDefault: false,
				showInSelector: args.showInSelector ?? true,
				logoUrl: args.logoUrl ?? null,
				defaultLinearTeamId: args.defaultLinearTeamId ?? null,
				defaultEmailAddressId: args.defaultEmailAddressId ?? null,
				showDocs: args.showDocs ?? true,
				showChangelog: args.showChangelog ?? true,
				showRoadmap: args.showRoadmap ?? true,
				showSupport: args.showSupport ?? true,
				landingSection: args.landingSection ?? null,
				overridesAuth: args.overridesAuth ?? false,
				isGoogleAuthRequired: args.isGoogleAuthRequired ?? false,
				googleDomain: args.googleDomain ?? null,
				overridesCustomLinks: args.overridesCustomLinks ?? false,
				usePortalLogoUrl: args.usePortalLogoUrl ?? false,
				portalLogoUrl: args.portalLogoUrl ?? null,
				changelogLink1Description: args.changelogLink1Description ?? null,
				changelogLink1Url: args.changelogLink1Url ?? null,
				changelogLink2Description: args.changelogLink2Description ?? null,
				changelogLink2Url: args.changelogLink2Url ?? null,
				overridesTheme: args.overridesTheme ?? false,
				darkMode: args.darkMode ?? null,
				accentColor: args.accentColor ?? null,
				accentTextColor: args.accentTextColor ?? null,
				buttonTextColor: args.buttonTextColor ?? null,
				darkModeAccentColor: args.darkModeAccentColor ?? null,
				darkModeAccentTextColor: args.darkModeAccentTextColor ?? null,
				darkModeButtonTextColor: args.darkModeButtonTextColor ?? null,
				backgroundColor: args.backgroundColor ?? null,
				darkModeBackgroundColor: args.darkModeBackgroundColor ?? null,
				portalEmbedImageUrl: args.portalEmbedImageUrl ?? null,
				workspaceId: authData.workspaceId,
				createdAt: now,
				updatedAt: now,
			})
		},
	),
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdatePortalInstanceArgs }) => {
			assertPortalLandingValue(args.landingSection)
			await tx.mutate.portalInstances.update({
				id: args.id,
				slug: args.slug,
				name: args.name,
				isDefault: args.isDefault,
				showInSelector: args.showInSelector,
				// `undefined` leaves the field untouched; `null` clears the stored URL.
				logoUrl: args.logoUrl,
				defaultLinearTeamId: args.defaultLinearTeamId,
				defaultEmailAddressId: args.defaultEmailAddressId,
				showDocs: args.showDocs,
				showChangelog: args.showChangelog,
				showRoadmap: args.showRoadmap,
				showSupport: args.showSupport,
				landingSection: args.landingSection,
				overridesAuth: args.overridesAuth,
				isGoogleAuthRequired: args.isGoogleAuthRequired,
				googleDomain: args.googleDomain,
				overridesCustomLinks: args.overridesCustomLinks,
				usePortalLogoUrl: args.usePortalLogoUrl,
				portalLogoUrl: args.portalLogoUrl,
				changelogLink1Description: args.changelogLink1Description,
				changelogLink1Url: args.changelogLink1Url,
				changelogLink2Description: args.changelogLink2Description,
				changelogLink2Url: args.changelogLink2Url,
				overridesTheme: args.overridesTheme,
				darkMode: args.darkMode,
				accentColor: args.accentColor,
				accentTextColor: args.accentTextColor,
				buttonTextColor: args.buttonTextColor,
				darkModeAccentColor: args.darkModeAccentColor,
				darkModeAccentTextColor: args.darkModeAccentTextColor,
				darkModeButtonTextColor: args.darkModeButtonTextColor,
				backgroundColor: args.backgroundColor,
				darkModeBackgroundColor: args.darkModeBackgroundColor,
				portalEmbedImageUrl: args.portalEmbedImageUrl,
				updatedAt: Date.now(),
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["portalInstances"]>
		}) => {
			await tx.mutate.portalInstances.delete({ id: args.id })
		},
	),
}
